<script lang="ts" setup>
import OmniDialog from "@/components/OmniDialog.vue";
import { useI18n } from "vue-i18n";
import { computed, ref, watch } from "vue";
import type { TruckDriverDto, TruckDto, TruckOwnerDto } from "@/api/trucks/dto/truck.dto";
import TruckLink from "@/components/links/TruckLink.vue";
import RouteParticipant from "@/views/trips/view/components/RouteParticipant.vue";
import TruckStatusChip from "@/components/chips/TruckStatusChip.vue";
import OwnerCompanyInfo from "@/components/user/OwnerCompanyInfo.vue";
import { TruckStatus, TruckStatusNames } from "@/data/truck";
import type { TripRoute } from "@/views/trips/edit/trip";
import type { TripOverviewRoute, TripRouteDto } from "@/api/trips/dto/trip.dto";
import { useTrip } from "@/composables";
import type { DriverDto } from "@/api/users/dto/driver.dto";
import type { OwnerCompany, OwnerDto } from "@/api/users/dto/owner.dto";
import { API } from "@/api/api";
import { useError } from "@/composables/useError";

const { t } = useI18n();
const { getRouteOwner, getRouteDriver, getRouteDriver2 } = useTrip();
const { showAPIError } = useError();

const api = new API();

const model = defineModel<boolean>({ default: false });

const props = defineProps<{
  title: string;
  route: TripRoute | TripRouteDto | TripOverviewRoute;
  truck: TruckDto;
}>();

const routeDriver = ref<DriverDto | TruckDriverDto | null>(null);
const routeDriver2 = ref<DriverDto | TruckDriverDto | null>(null);
const routeOwner = ref<OwnerDto | TruckOwnerDto | null>(null);
const routeOwnerCompany = ref<OwnerCompany | null>(null);
const truckOwnerCompany = ref<OwnerCompany | null>(null);
const loading = ref<boolean>(false);

const isDriverDiff = computed(() => {
  return routeDriver.value?.id !== props.truck.driverId;
});

const isDriver2Diff = computed(() => {
  if (!routeDriver2.value) {
    return false;
  }

  return routeDriver2.value?.id !== props.truck.driver2Id;
});

const isOwnerDiff = computed(() => {
  return routeOwner.value?.id !== props.truck.ownerId;
});

const isOwnerCompanyDiff = computed(() => {
  return routeDriver.value?.ownerCompanyId !== props.truck.driver?.ownerCompanyId;
});

const isTruckInvalidStatus = computed(() => {
  if (!props.truck.status) {
    return true;
  }

  const invalidStatus = [TruckStatus.InRoute];

  return invalidStatus.includes(props.truck.status);
});

const hasDiff = computed(() => {
  const isDriverIdDiff = props.route.driverId !== props.truck.driverId;
  const isDriver2IdDiff = props.route.driver2Id !== props.truck.driver2Id;
  const isOwnerIdDiff = props.route.ownerId !== props.truck.ownerId;
  const isOwnerCompanyIdDiff = props.route.ownerCompanyId !== props.truck.driver?.ownerCompanyId;

  return isDriverIdDiff || isDriver2IdDiff || isOwnerIdDiff || isOwnerCompanyIdDiff || isTruckInvalidStatus.value;
});

const invalidMessage = computed(() => {
  const reasons: string[] = [];

  if (isTruckInvalidStatus.value && props.truck.status) {
    reasons.push(
      t("undo.reasons.truck-status", {
        number: props.truck.number,
        status: TruckStatusNames[props.truck.status],
      }),
    );
  }

  if (isOwnerDiff.value) {
    reasons.push(
      t("undo.reasons.owner-changed", {
        oldOwner: routeOwner.value?.name,
        newOwner: props.truck.owner.name,
      }),
    );
  }

  if (isDriverDiff.value) {
    reasons.push(
      t("undo.reasons.driver-changed", {
        oldDriver: routeDriver.value?.name,
        newDriver: props.truck.driver?.name,
      }),
    );
  }

  if (isDriver2Diff.value) {
    reasons.push(
      t("undo.reasons.driver2-changed", {
        oldDriver: routeDriver2.value?.name,
        newDriver: props.truck.driver2?.name,
      }),
    );
  }

  if (isOwnerCompanyDiff.value) {
    reasons.push(
      t("undo.reasons.owner-company-changed", {
        oldCompany: routeOwnerCompany.value?.name,
        newCompany: truckOwnerCompany.value?.name,
      }),
    );
  }

  if (reasons.length === 0) {
    return "";
  }

  return t("undo.reasons.header") + "\n" + reasons.join("\n");
});

async function loadOwnerCompany(
  ownerId: number | null | undefined,
  ownerCompanyId: number | null | undefined,
): Promise<OwnerCompany | null> {
  if (!ownerId || !ownerCompanyId) {
    return null;
  }
  try {
    const response = await api.users.findOwnerCompanyById(ownerId, ownerCompanyId);

    if (response.errorData) {
      showAPIError(response);
      return null;
    }

    return response.data || null;
  } catch (error) {
    console.error(error);
    return null;
  }
}

async function loadInfo() {
  loading.value = true;

  try {
    const [driver, driver2, owner] = await Promise.all([
      getRouteDriver(props.route.driverId, props.truck),
      getRouteDriver2(props.route.driver2Id, props.truck),
      getRouteOwner(props.route.ownerId, props.truck),
    ]);

    routeDriver.value = driver;
    routeDriver2.value = driver2;
    routeOwner.value = owner;

    const routeOwnerId = owner?.id;
    const truckOwnerId = props.truck.ownerId;
    const routeOwnerCompanyId = driver?.ownerCompanyId;
    const truckOwnerCompanyId = props.truck?.driver?.ownerCompanyId;

    if (routeOwnerId === truckOwnerId && routeOwnerCompanyId === truckOwnerCompanyId) {
      const company = await loadOwnerCompany(routeOwnerId, routeOwnerCompanyId);
      routeOwnerCompany.value = company;
      truckOwnerCompany.value = company;
    } else {
      routeOwnerCompany.value = await loadOwnerCompany(routeOwnerId, routeOwnerCompanyId);
      truckOwnerCompany.value = await loadOwnerCompany(truckOwnerId, truckOwnerCompanyId);
    }
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
}

defineExpose({
  hasDiff,
});

watch(
  () => model,
  async (newValue) => {
    if (newValue) {
      await loadInfo();
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <omni-dialog v-model="model" :loading="loading" :title="props.title" :width="1000">
    <span class="text-grey-darken-3">{{ t("trip.truck-details-from-route") }}:</span>
    <v-row class="mt-1">
      <v-col cols="6">
        <div class="bg-grey-lighten-4 rounded-lg pa-4">
          <truck-link
            v-if="truck.id"
            :trailer-number="truck.trailer?.number"
            :truck-id="truck.id"
            :truck-number="truck.number"
          />

          <route-participant v-if="routeDriver" :user="routeDriver" class="mt-4" />

          <route-participant v-if="routeDriver2" :user="routeDriver2" class="mt-3" is-driver2 />

          <route-participant v-if="routeOwner" :user="routeOwner" class="mt-3" />

          <div v-if="routeOwnerCompany" class="bg-white pa-4 rounded-lg mt-5">
            <owner-company-info :company="routeOwnerCompany" hide-doc-number hide-insurance-exp-exp />
          </div>
        </div>
      </v-col>

      <v-col cols="6">
        <div class="bg-grey-lighten-4 rounded-lg pa-4">
          <div class="d-flex col-gap-3">
            <truck-link
              v-if="truck.id"
              :trailer-number="truck.trailer?.number"
              :truck-id="truck.id"
              :truck-number="truck.number"
            />

            <truck-status-chip :truck="truck" />
          </div>

          <route-participant v-if="truck.driver" :user="truck.driver" class="mt-4" />

          <route-participant v-if="truck.driver2" :user="truck.driver2" class="mt-3" is-driver2 />

          <route-participant v-if="truck.owner" :user="truck.owner" class="mt-3" />

          <div v-if="truckOwnerCompany" class="bg-white pa-4 rounded-lg mt-5">
            <owner-company-info :company="truckOwnerCompany" hide-doc-number hide-insurance-exp-exp />
          </div>
        </div>
      </v-col>
    </v-row>

    <p v-if="invalidMessage" class="mt-3 text-danger white-space-pre-line">
      {{ invalidMessage }}
    </p>
  </omni-dialog>
</template>
